/**
 * Modal para seleccionar imágenes desde: subida propia, capturas, materiales o academia.
 *
 * CDN: Usamos el CDN de Azure Front Door ya desplegado. Ventajas: caché, menor latencia,
 * menos carga en origen. Las URLs relativas (capture-media/..., material-images/..., blog-images/...)
 * se resuelven a la URL completa del CDN.
 */
import { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Upload, Image as ImageIcon, ShoppingBag, Video, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';

const CDN_BASE_URL = 'https://aipeces-media-a4hsbvf5gff3b6b0.z02.azurefd.net';

export type ImagePickerSelectMeta = { creditUsername?: string };

interface ImagePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (url: string, meta?: ImagePickerSelectMeta) => void;
    /** Título del artículo para sugerir la familia de producto al abrir Tienda. */
    contextTitle?: string;
}

interface CaptureSearchResult {
    capture_id: string;
    species: string;
    display_name: string;
    image_url: string;
    created_at: string;
}

interface MaterialRow {
    id: string;
    title: string;
    images?: string[] | null;
}

interface StoreProductRow {
    id: number;
    nombre: string;
    marca: string;
    categoria: string;
    imagen_principal: string;
    imagen_alt: string;
}

interface StoreProductsResponse {
    items?: StoreProductRow[];
}

interface AcademyVideoRow {
    id: string;
    title: string;
    thumbnail_url: string | null;
    video_url: string;
}

function resolveImageUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${CDN_BASE_URL}/${url.replace(/^\//, '')}`;
}

function storeSearchFromArticleTitle(title: string): string {
    const normalized = title.trim().toLowerCase();
    const families: Array<[RegExp, string]> = [
        [/\b(fluorocarbono|trenzado|hilo|línea|linea)\b/, 'hilo'],
        [/\b(caña|canas|cañas)\b/, 'caña'],
        [/\bcarrete(s)?\b/, 'carrete'],
        [/\btransductor(es)?\b/, 'transductor'],
        [/\b(ecosonda|sonda)(s)?\b/, 'sonda'],
        [/\bbatería(s)?\b/, 'batería'],
        [/\bvinilo(s)?\b/, 'vinilo'],
        [/\b(jig|jigs|jigging)\b/, 'jig'],
        [/\b(señuelo|senuelo)(s)?\b/, 'señuelo'],
        [/\banzuelo(s)?\b/, 'anzuelo'],
    ];
    return families.find(([pattern]) => pattern.test(normalized))?.[1] ?? '';
}

export function ImagePickerModal({
    isOpen,
    onClose,
    onSelect,
    contextTitle = '',
}: ImagePickerModalProps) {
    const [uploading, setUploading] = useState(false);
    const [captureSearch, setCaptureSearch] = useState('');
    const [storeSearch, setStoreSearch] = useState(() => storeSearchFromArticleTitle(contextTitle));

    useEffect(() => {
        if (!isOpen || storeSearch.trim()) return;
        setStoreSearch(storeSearchFromArticleTitle(contextTitle));
    }, [contextTitle, isOpen, storeSearch]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;
        setUploading(true);
        try {
            const path = `blog/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            const result = await apiClient.uploadFile('blog-images', path, file);
            if (result.error) throw new Error(result.error.message);
            const data = result.data as { path?: string; url?: string; publicUrl?: string } | undefined;
            const url = data?.publicUrl ?? data?.url ?? data?.path;
            if (url) {
                const fullUrl = url.startsWith('http') ? url : `${CDN_BASE_URL}/${url}`;
                onSelect(fullUrl);
                onClose();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Seleccionar imagen</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="upload">
                    <TabsList className="grid w-full grid-cols-5">
                        <TabsTrigger value="upload" className="flex items-center gap-1.5">
                            <Upload size={14} />
                            Subir
                        </TabsTrigger>
                        <TabsTrigger value="captures" className="flex items-center gap-1.5">
                            <ImageIcon size={14} />
                            Capturas
                        </TabsTrigger>
                        <TabsTrigger value="materials" className="flex items-center gap-1.5">
                            <ShoppingBag size={14} />
                            Materiales
                        </TabsTrigger>
                        <TabsTrigger value="store" className="flex items-center gap-1.5">
                            <ShoppingBag size={14} />
                            Tienda
                        </TabsTrigger>
                        <TabsTrigger value="academy" className="flex items-center gap-1.5">
                            <Video size={14} />
                            Academia
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="upload" className="mt-4">
                        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 p-8 cursor-pointer hover:bg-muted/50 transition-colors">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleUpload}
                                disabled={uploading}
                                className="hidden"
                            />
                            {uploading ? (
                                <Loader2 size={32} className="animate-spin text-muted-foreground" />
                            ) : (
                                <Upload size={32} className="text-muted-foreground" />
                            )}
                            <span className="text-sm text-muted-foreground">
                                {uploading ? 'Subiendo…' : 'Haz clic o arrastra una imagen'}
                            </span>
                        </label>
                    </TabsContent>
                    <TabsContent value="captures" className="mt-4">
                        <CapturesTab
                            search={captureSearch}
                            onSearchChange={setCaptureSearch}
                            onSelect={(url, meta) => {
                                onSelect(url, meta);
                                onClose();
                            }}
                        />
                    </TabsContent>
                    <TabsContent value="materials" className="mt-4">
                        <MaterialsTab
                            onSelect={(url) => {
                                onSelect(url);
                                onClose();
                            }}
                        />
                    </TabsContent>
                    <TabsContent value="store" className="mt-4">
                        <StoreProductsTab
                            search={storeSearch}
                            onSearchChange={setStoreSearch}
                            onSelect={(url) => {
                                onSelect(url);
                                onClose();
                            }}
                        />
                    </TabsContent>
                    <TabsContent value="academy" className="mt-4">
                        <AcademyTab
                            onSelect={(url) => {
                                onSelect(url);
                                onClose();
                            }}
                        />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function StoreProductsTab({
    search,
    onSearchChange,
    onSelect,
}: {
    search: string;
    onSearchChange: (v: string) => void;
    onSelect: (url: string) => void;
}) {
    const [debounced, setDebounced] = useState(search);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleChange = (value: string) => {
        onSearchChange(value);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setDebounced(value.trim()), 300);
    };

    const { data: products = [], isLoading } = useQuery({
        queryKey: ['blog-picker-store-products', debounced],
        queryFn: async () => {
            const result = await apiClient.rpc<StoreProductsResponse>('get_catalog_products', {
                p_search: debounced || null,
                p_marca: null,
                p_categoria: null,
                p_pesca_tag: null,
                p_coleccion_slug: null,
                p_precio_min: null,
                p_precio_max: null,
                p_core_only: false,
                p_limit: 40,
                p_offset: 0,
            });
            return Array.isArray(result.data?.items) ? result.data.items : [];
        },
    });

    return (
        <div className="space-y-3">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="Buscar hilo, caña, sonda, señuelo..."
                    className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                />
            </div>
            <p className="text-xs text-muted-foreground">
                En guías genéricas, elige una foto neutra. Reserva marcas visibles para artículos de un modelo concreto.
            </p>
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : products.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                    No hay productos con imagen para esta búsqueda
                </p>
            ) : (
                <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto">
                    {products
                        .filter((product) => Boolean(product.imagen_principal))
                        .map((product) => (
                            <button
                                key={product.id}
                                type="button"
                                onClick={() => onSelect(resolveImageUrl(product.imagen_principal))}
                                title={`${product.nombre} · ${product.marca || product.categoria}`}
                                className="overflow-hidden rounded-lg border border-border text-left hover:border-primary focus:ring-2 focus:ring-primary"
                            >
                                <img
                                    src={resolveImageUrl(product.imagen_principal)}
                                    alt={product.imagen_alt || product.nombre}
                                    className="aspect-video w-full bg-white object-contain"
                                    loading="lazy"
                                />
                                <span className="block truncate px-2 py-1.5 text-xs">{product.nombre}</span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
}

function CapturesTab({
    search,
    onSearchChange,
    onSelect,
}: {
    search: string;
    onSearchChange: (v: string) => void;
    onSelect: (url: string, meta?: ImagePickerSelectMeta) => void;
}) {
    const [debounced, setDebounced] = useState(search);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleChange = (v: string) => {
        onSearchChange(v);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setDebounced(v), 400);
    };

    const { data: items = [], isLoading } = useQuery({
        queryKey: ['capture-images', debounced],
        queryFn: async () => {
            const r = await apiClient.rpc<CaptureSearchResult[]>('admin_search_capture_images', {
                p_search: debounced,
                p_limit: 24,
            });
            return r.data ?? [];
        },
        enabled: true,
    });

    return (
        <div className="space-y-3">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="Buscar por especie o usuario..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
                />
            </div>
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : items.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">Sin resultados</p>
            ) : (
                <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                    {items.map((item) => {
                        const url = resolveImageUrl(item.image_url);
                        return (
                            <button
                                key={item.capture_id}
                                type="button"
                                onClick={() => onSelect(url, {
                                    creditUsername: item.display_name?.trim() || undefined,
                                })}
                                className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary focus:ring-2 focus:ring-primary"
                            >
                                <img src={url} alt={item.species} className="w-full h-full object-cover" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function MaterialsTab({ onSelect }: { onSelect: (url: string) => void }) {
    const { data: materials = [], isLoading } = useQuery({
        queryKey: ['blog-picker-materials'],
        queryFn: async () => {
            const r = await apiClient.rpc<MaterialRow[]>('admin_get_materials', {
                p_status_filter: 'approved',
                p_limit: 50,
            });
            return r.data ?? [];
        },
    });

    const withImages = materials.filter((m) => Array.isArray(m.images) && m.images.length > 0);

    return (
        <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
            {isLoading ? (
                <div className="col-span-4 flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : (
                withImages.slice(0, 24).map((m) => {
                    const img = Array.isArray(m.images) ? m.images[0] : null;
                    const url = img && typeof img === 'string' ? img : null;
                    const fullUrl = url ? resolveImageUrl(url) : null;
                    if (!fullUrl) return null;
                    return (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => onSelect(fullUrl)}
                            className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary"
                        >
                            <img src={fullUrl} alt={m.title} className="w-full h-full object-cover" />
                        </button>
                    );
                })
            )}
        </div>
    );
}

function AcademyTab({ onSelect }: { onSelect: (url: string) => void }) {
    const { data: videos = [], isLoading } = useQuery({
        queryKey: ['blog-picker-academy'],
        queryFn: async () => {
            const r = await apiClient.rpc<AcademyVideoRow[]>('admin_get_academy_videos', {
                p_status_filter: 'approved',
                p_limit: 20,
            });
            return r.data ?? [];
        },
    });

    return (
        <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
            {isLoading ? (
                <div className="col-span-2 flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : (
                videos.slice(0, 12).map((v) => {
                    const thumb = v.thumbnail_url ? resolveImageUrl(v.thumbnail_url) : null;
                    const videoUrl = v.video_url;
                    return (
                        <button
                            key={v.id}
                            type="button"
                            onClick={() => onSelect(videoUrl)}
                            className="flex gap-2 rounded-lg border border-border hover:border-primary p-2 text-left"
                        >
                            {thumb && (
                                <img src={thumb} alt="" className="w-16 h-12 object-cover rounded shrink-0" />
                            )}
                            <span className="text-sm truncate flex-1 min-w-0">{v.title}</span>
                        </button>
                    );
                })
            )}
        </div>
    );
}
